/**
 * Pure helpers for reading a Playbook's flow graph (nodes/edges) — no React,
 * no component state. Used by App.jsx's playbook-application logic (URL
 * `?playbookId=`, default-playbook-on-mount, restoring a saved conversation's
 * playbook) to resolve which agent(s) a playbook exposes and which one is
 * the entry point.
 */

export function getPlaybookAgentRefs(playbook) {
    return ((playbook.flow?.nodes) || [])
        .filter(n => n.type === 'agent' && n.ref)
        .map(n => ({ itemId: n.ref, id: n.ref, name: n.ref }));
}

export function resolveRefNames(items) {
    return (items || []).map(r => (typeof r === 'string' ? r : (r.name || r.itemId))).filter(Boolean);
}

// Compute the scoped ADK app_name: {playbook_snake}_{agent_snake}
// Mirrors catalog_agent_service._snake() on the backend.
export function scopedAgentName(playbookName, agentRef) {
    const snake = s => (s || '').toLowerCase().replace(/[-\s.]+/g, '_');
    return `${snake(playbookName)}_${snake(agentRef)}`;
}

// Find the root agent in a playbook's flow graph — the agent that has no
// incoming edges from other nodes (i.e., the orchestrator / entry point).
export function getRootAgentRef(playbook) {
    const refs = getPlaybookAgentRefs(playbook);
    if (refs.length === 0) return null;
    if (refs.length === 1) return refs[0];
    const flow = playbook.flow || {};
    const nodes = flow.nodes || [];
    const edges = flow.edges || [];
    if (edges.length === 0) return refs[0];
    // Build ref-string → node id map
    const refToNodeId = {};
    for (const n of nodes) {
        if (n.type === 'agent' && n.ref) refToNodeId[n.ref] = n.id || n.ref;
    }
    // IDs that appear as edge targets (i.e. are sub-agents)
    const targetIds = new Set(edges.map(e => e.target || e.to).filter(Boolean));
    for (const ref of refs) {
        const refId = ref.itemId || ref.id || ref.name;
        const nodeId = refToNodeId[refId] || refId;
        if (!targetIds.has(nodeId)) return ref;
    }
    return refs[0]; // fallback if graph is cyclic
}
